import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import Pagination from "../../../component/library/pagination";
import {
  cantonFairLeadAdminApiService,
  downloadCantonFairLeadsExcel,
} from "../../../service/admin/cantonFairLeadAdminApiService";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import * as Opt from "./formOptions";
import "./style.css";

function purposesLabel(row) {
  if (!row?.purposes?.length) return "—";
  return row.purposes
    .map((p) => (p === "Other" && row.otherPurposeDetails ? `Other (${row.otherPurposeDetails})` : p))
    .join(", ");
}

function mapRowToForm(row) {
  return {
    campaignSlug: row.campaignSlug || "canton2026",
    purposes: [...(row.purposes || [])],
    otherPurposeDetails: row.otherPurposeDetails || "",
    fullName: row.fullName || "",
    companyName: row.companyName || "",
    mobile: row.mobile || "",
    email: row.email || "",
    city: row.city || "",
    state: row.state || "",
    country: row.country || "",
    heardAbout: row.heardAbout || "",
    categories: [...(row.categories || [])],
    platforms: [...(row.platforms || [])],
    step3BrandName: row.step3BrandName || "",
    expansionGoal: row.expansionGoal || "",
    expectedSku: row.expectedSku || "",
    moq: row.moq || "",
    currentSource: row.currentSource || "",
    budget: row.budget || "",
    challenge: row.challenge || "",
    resellerInterest: row.resellerInterest || "",
    adminNotes: row.adminNotes || "",
  };
}

function toggleInArray(arr, val) {
  if (arr.includes(val)) return arr.filter((x) => x !== val);
  return [...arr, val];
}

function sanitizePhoneInput(raw) {
  let v = String(raw ?? "").replace(/[^\d+]/g, "");
  v = v.replace(/(?!^)\+/g, "");
  return v.slice(0, 16);
}

export default function CantonFairLeadsAdmin() {
  const { showToast } = useMessage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [q, setQ] = useState("");
  const [qDraft, setQDraft] = useState("");
  const [purposeFilter, setPurposeFilter] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [pageSize, setPageSize] = useState(20);
  const [dateFromDraft, setDateFromDraft] = useState("");
  const [dateToDraft, setDateToDraft] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [exporting, setExporting] = useState(false);

  const [viewRow, setViewRow] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchList = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("limit", String(pageSize));
    if (q.trim()) params.set("q", q.trim());
    if (purposeFilter) params.set("purpose", purposeFilter);
    if (campaignFilter.trim()) params.set("campaignSlug", campaignFilter.trim().toLowerCase());
    if (dateFrom.trim()) params.set("dateFrom", dateFrom.trim());
    if (dateTo.trim()) params.set("dateTo", dateTo.trim());
    const qs = `?${params.toString()}`;
    cantonFairLeadAdminApiService
      .list(qs)
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) {
          setRows(res.data.results || []);
          setPages({
            currentPage: res.data.currentPage || 1,
            totalPages: res.data.totalPages || 1,
            totalItems: res.data.total || 0,
          });
        } else {
          showToast(res?.status?.message || "Failed to load leads", ToastTypes.WARNING);
        }
      })
      .catch(() => showToast("Failed to load leads", ToastTypes.ERROR))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, q, purposeFilter, campaignFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const applySearch = () => {
    setQ(qDraft);
    setDateFrom(dateFromDraft.trim());
    setDateTo(dateToDraft.trim());
    setPage(1);
  };

  const buildFilterQueryString = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (purposeFilter) params.set("purpose", purposeFilter);
    if (campaignFilter.trim()) params.set("campaignSlug", campaignFilter.trim().toLowerCase());
    if (dateFrom.trim()) params.set("dateFrom", dateFrom.trim());
    if (dateTo.trim()) params.set("dateTo", dateTo.trim());
    const s = params.toString();
    return s ? `?${s}` : "";
  };

  const handleExportCsv = () => {
    setExporting(true);
    downloadCantonFairLeadsExcel(buildFilterQueryString())
      .then(() => showToast("Excel exported", ToastTypes.SUCCESS))
      .catch((err) => showToast(err?.message || "Export failed", ToastTypes.ERROR))
      .finally(() => setExporting(false));
  };

  const confirmDeleteLead = () => {
    if (!deleteTarget?._id) return;
    setDeleting(true);
    cantonFairLeadAdminApiService
      .remove(deleteTarget._id)
      .then((res) => {
        if (res?.status?.code === 200) {
          showToast("Lead deleted", ToastTypes.SUCCESS);
          setDeleteTarget(null);
          fetchList();
        } else {
          showToast(res?.status?.message || "Delete failed", ToastTypes.WARNING);
        }
      })
      .catch(() => showToast("Delete failed", ToastTypes.ERROR))
      .finally(() => setDeleting(false));
  };

  const openEdit = (row) => {
    setEditId(row._id);
    setEditForm(mapRowToForm(row));
  };

  const saveEdit = () => {
    if (!editForm || !editId) return;
    if (editForm.purposes.includes("Other") && !String(editForm.otherPurposeDetails || "").trim()) {
      showToast("Please specify purpose when “Other” is selected", ToastTypes.WARNING);
      return;
    }
    const payload = {
      ...editForm,
      campaignSlug: (editForm.campaignSlug || "canton2026").toLowerCase().trim(),
      platforms: editForm.platforms || [],
    };
    setSaving(true);
    cantonFairLeadAdminApiService
      .update(editId, payload)
      .then((res) => {
        if (res?.status?.code === 200) {
          showToast("Saved", ToastTypes.SUCCESS);
          setEditForm(null);
          setEditId(null);
          fetchList();
        } else {
          showToast(res?.status?.message || res?.error?.message || "Save failed", ToastTypes.WARNING);
        }
      })
      .catch((err) => showToast(err?.message || "Save failed", ToastTypes.ERROR))
      .finally(() => setSaving(false));
  };

  return (
    <div className="cfl-page">
      <div className="cfl-header">
        <div>
          <h2 className="cfl-title">Canton Fair leads</h2>
          <p className="cfl-sub">Importerr.com multi-step fair questionnaire submissions</p>
        </div>
        <div style={{ color: "#64748b", fontSize: 14 }}>
          Total: <strong>{pages.totalItems}</strong>
        </div>
      </div>

      <div className="cfl-toolbar">
        <input
          type="search"
          className="cfl-toolbar-search"
          placeholder="Search name, email, mobile, company, brand, city…"
          title="Name, email, mobile, company, brand, city"
          value={qDraft}
          onChange={(e) => setQDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applySearch()}
        />
        <button type="button" className="cfl-toolbar-btn cfl-toolbar-btn-primary" onClick={applySearch}>
          Search
        </button>
        <select
          className="cfl-toolbar-select cfl-toolbar-purpose"
          value={purposeFilter}
          onChange={(e) => {
            setPurposeFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All purposes</option>
          {Opt.PURPOSE_IDS.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <input
          type="text"
          className="cfl-toolbar-input cfl-toolbar-campaign"
          placeholder="Campaign"
          title="Campaign slug, e.g. canton2026"
          value={campaignFilter}
          onChange={(e) => {
            setCampaignFilter(e.target.value);
            setPage(1);
          }}
        />
        <label className="cfl-date-lbl">
          <span className="cfl-date-lbl-text">From</span>
          <input type="date" value={dateFromDraft} onChange={(e) => setDateFromDraft(e.target.value)} className="cfl-date-input" />
        </label>
        <label className="cfl-date-lbl">
          <span className="cfl-date-lbl-text">To</span>
          <input type="date" value={dateToDraft} onChange={(e) => setDateToDraft(e.target.value)} className="cfl-date-input" />
        </label>
        <label className="cfl-rows-inline">
          <span className="cfl-rows-inline-text">Rows</span>
          <select
            className="cfl-toolbar-select cfl-toolbar-rows"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value) || 20);
              setPage(1);
            }}
          >
            {[10, 20, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="cfl-toolbar-btn cfl-toolbar-btn-secondary"
          onClick={() => {
            setQDraft("");
            setQ("");
            setPurposeFilter("");
            setCampaignFilter("");
            setDateFromDraft("");
            setDateToDraft("");
            setDateFrom("");
            setDateTo("");
            setPage(1);
          }}
        >
          Reset
        </button>
        <button
          type="button"
          className="cfl-toolbar-btn cfl-toolbar-btn-export"
          onClick={handleExportCsv}
          disabled={exporting}
          title="Download Excel with current filters"
        >
          {exporting ? "Export…" : "Export Excel"}
        </button>
      </div>

      {loading ? (
        <div className="cfl-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="cfl-empty">No submissions found.</div>
      ) : (
        <>
          <div className="cfl-table-wrap">
            <table className="cfl-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Mobile</th>
                  <th>Email</th>
                  <th>Purposes</th>
                  <th>Brand</th>
                  <th>City</th>
                  <th>Campaign</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row._id}>
                    <td>{(pages.currentPage - 1) * pageSize + idx + 1}</td>
                    <td>{row.fullName || "—"}</td>
                    <td>{row.mobile || "—"}</td>
                    <td>{row.email || "—"}</td>
                    <td className="cfl-purposes">{purposesLabel(row)}</td>
                    <td>{row.step3BrandName || "—"}</td>
                    <td>{row.city || "—"}</td>
                    <td>{row.campaignSlug || "—"}</td>
                    <td>{row.createdAt ? moment(row.createdAt).format("DD MMM YYYY, HH:mm") : "—"}</td>
                    <td>
                      <div className="cfl-actions">
                        <button type="button" onClick={() => setViewRow(row)}>
                          View
                        </button>
                        <button type="button" onClick={() => openEdit(row)}>
                          Edit
                        </button>
                        <button type="button" className="cfl-danger" onClick={() => setDeleteTarget(row)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages.totalItems > 0 && (
            <div className="cfl-pagination-wrap">
              <Pagination pages={pages} page={page} setPage={setPage} />
            </div>
          )}
        </>
      )}

      {viewRow && (
        <div className="cfl-modal-overlay" role="dialog" aria-modal="true">
          <div className="cfl-modal">
            <div className="cfl-modal-header">
              <h3>Lead details</h3>
              <button type="button" className="cfl-modal-close" onClick={() => setViewRow(null)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="cfl-modal-body">
              <dl className="cfl-field-grid">
                <dt>Submitted</dt>
                <dd>{viewRow.createdAt ? moment(viewRow.createdAt).format("DD MMM YYYY, HH:mm") : "—"}</dd>
                <dt>Campaign</dt>
                <dd>{viewRow.campaignSlug || "—"}</dd>
                <dt>Purposes</dt>
                <dd>{purposesLabel(viewRow)}</dd>
                <dt>Full name</dt>
                <dd>{viewRow.fullName || "—"}</dd>
                <dt>Company</dt>
                <dd>{viewRow.companyName || "—"}</dd>
                <dt>Mobile</dt>
                <dd>{viewRow.mobile || "—"}</dd>
                <dt>Email</dt>
                <dd>{viewRow.email || "—"}</dd>
                <dt>Location</dt>
                <dd>{[viewRow.city, viewRow.state, viewRow.country].filter(Boolean).join(", ") || "—"}</dd>
                <dt>Heard about</dt>
                <dd>{viewRow.heardAbout || "—"}</dd>
                <dt>Categories</dt>
                <dd>{(viewRow.categories || []).join(", ") || "—"}</dd>
                <dt>Platforms</dt>
                <dd>{(viewRow.platforms || []).join(", ") || "—"}</dd>
                <dt>Brand name</dt>
                <dd>{viewRow.step3BrandName || "—"}</dd>
                <dt>Expansion</dt>
                <dd>{viewRow.expansionGoal || "—"}</dd>
                <dt>Expected SKU</dt>
                <dd>{viewRow.expectedSku || "—"}</dd>
                <dt>MOQ</dt>
                <dd>{viewRow.moq || "—"}</dd>
                <dt>Current source</dt>
                <dd>{viewRow.currentSource || "—"}</dd>
                <dt>Budget</dt>
                <dd>{viewRow.budget || "—"}</dd>
                <dt>Challenge</dt>
                <dd style={{ whiteSpace: "pre-wrap" }}>{viewRow.challenge || "—"}</dd>
                <dt>Reseller interest</dt>
                <dd>{viewRow.resellerInterest || "—"}</dd>
                <dt>Admin notes</dt>
                <dd style={{ whiteSpace: "pre-wrap" }}>{viewRow.adminNotes || "—"}</dd>
                <dt>IP / UA</dt>
                <dd style={{ wordBreak: "break-all", fontSize: 12 }}>
                  {(viewRow.metadata && viewRow.metadata.ip) || "—"} / {(viewRow.metadata && viewRow.metadata.userAgent) || "—"}
                </dd>
              </dl>
            </div>
            <div className="cfl-modal-footer">
              <button type="button" className="cfl-muted" onClick={() => setViewRow(null)}>
                Close
              </button>
              <button
                type="button"
                className="cfl-primary"
                onClick={() => {
                  openEdit(viewRow);
                  setViewRow(null);
                }}
              >
                Edit this lead
              </button>
            </div>
          </div>
        </div>
      )}

      {editForm && (
        <div className="cfl-modal-overlay" role="dialog" aria-modal="true">
          <div className="cfl-modal" style={{ maxWidth: 760 }}>
            <div className="cfl-modal-header">
              <h3>Edit lead</h3>
              <button type="button" className="cfl-modal-close" onClick={() => { setEditForm(null); setEditId(null); }} aria-label="Close">
                ×
              </button>
            </div>
            <div className="cfl-modal-body">
              <div className="cfl-form-section">
                <h4>Meta</h4>
                <label className="cfl-check" style={{ display: "block", marginBottom: 8 }}>
                  Campaign slug
                  <input
                    className="cfl-input"
                    style={{ marginTop: 6 }}
                    value={editForm.campaignSlug}
                    onChange={(e) => setEditForm({ ...editForm, campaignSlug: e.target.value })}
                  />
                </label>
              </div>

              <div className="cfl-form-section">
                <h4>Purposes</h4>
                <div className="cfl-check-grid">
                  {Opt.PURPOSE_IDS.map((p) => (
                    <label key={p} className="cfl-check">
                      <input
                        type="checkbox"
                        checked={editForm.purposes.includes(p)}
                        onChange={() => setEditForm({ ...editForm, purposes: toggleInArray(editForm.purposes, p) })}
                      />
                      <span>{p}</span>
                    </label>
                  ))}
                </div>
                {editForm.purposes.includes("Other") && (
                  <label style={{ display: "block", marginTop: 10 }}>
                    <span style={{ fontSize: 13, color: "#64748b" }}>Other details</span>
                    <input
                      className="cfl-input"
                      style={{ marginTop: 4 }}
                      value={editForm.otherPurposeDetails}
                      onChange={(e) => setEditForm({ ...editForm, otherPurposeDetails: e.target.value })}
                    />
                  </label>
                )}
              </div>

              <div className="cfl-form-section">
                <h4>Contact</h4>
                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Full name</span>
                    <input className="cfl-input" value={editForm.fullName} onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })} />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Company</span>
                    <input className="cfl-input" value={editForm.companyName} onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })} />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Mobile (Indian or international)</span>
                    <input
                      className="cfl-input"
                      value={editForm.mobile}
                      onChange={(e) => setEditForm({ ...editForm, mobile: sanitizePhoneInput(e.target.value) })}
                    />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Email</span>
                    <input className="cfl-input" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>City</span>
                    <input className="cfl-input" value={editForm.city} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>State</span>
                    <input className="cfl-input" value={editForm.state} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} />
                  </label>
                  <label style={{ gridColumn: "1 / -1" }}>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Country</span>
                    <input className="cfl-input" value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} />
                  </label>
                  <label style={{ gridColumn: "1 / -1" }}>
                    <span style={{ fontSize: 12, color: "#64748b" }}>How did you hear about us?</span>
                    <select className="cfl-input" value={editForm.heardAbout} onChange={(e) => setEditForm({ ...editForm, heardAbout: e.target.value })}>
                      {Opt.HEARD_ABOUT.map((h) => (
                        <option key={h || "empty"} value={h}>
                          {h || "—"}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <div className="cfl-form-section">
                <h4>Categories</h4>
                <div className="cfl-check-grid" style={{ maxHeight: 200, overflowY: "auto", border: "1px solid #e2e8f0", padding: 10, borderRadius: 8 }}>
                  {Opt.PRODUCT_CATEGORIES.map((c) => (
                    <label key={c} className="cfl-check">
                      <input
                        type="checkbox"
                        checked={editForm.categories.includes(c)}
                        onChange={() => setEditForm({ ...editForm, categories: toggleInArray(editForm.categories, c) })}
                      />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="cfl-form-section">
                <h4>Platforms</h4>
                <div className="cfl-check-grid" style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #e2e8f0", padding: 10, borderRadius: 8 }}>
                  {Opt.PLATFORMS.map((c) => (
                    <label key={c} className="cfl-check">
                      <input
                        type="checkbox"
                        checked={editForm.platforms.includes(c)}
                        onChange={() => setEditForm({ ...editForm, platforms: toggleInArray(editForm.platforms, c) })}
                      />
                      <span>{c}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="cfl-form-section">
                <h4>Sourcing</h4>
                <div style={{ display: "grid", gap: 10 }}>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Brand name</span>
                    <input className="cfl-input" value={editForm.step3BrandName} onChange={(e) => setEditForm({ ...editForm, step3BrandName: e.target.value })} />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Expansion goal</span>
                    <select className="cfl-input" value={editForm.expansionGoal} onChange={(e) => setEditForm({ ...editForm, expansionGoal: e.target.value })}>
                      {Opt.EXPANSION_GOALS.map((h) => (
                        <option key={h || "e"} value={h}>
                          {h || "—"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <label>
                      <span style={{ fontSize: 12, color: "#64748b" }}>Expected SKU</span>
                      <select className="cfl-input" value={editForm.expectedSku} onChange={(e) => setEditForm({ ...editForm, expectedSku: e.target.value })}>
                        {Opt.EXPECTED_SKU.map((h) => (
                          <option key={h || "e"} value={h}>
                            {h || "—"}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span style={{ fontSize: 12, color: "#64748b" }}>MOQ</span>
                      <select className="cfl-input" value={editForm.moq} onChange={(e) => setEditForm({ ...editForm, moq: e.target.value })}>
                        {Opt.MOQ_OPTIONS.map((h) => (
                          <option key={h || "e"} value={h}>
                            {h || "—"}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Current source</span>
                    <select className="cfl-input" value={editForm.currentSource} onChange={(e) => setEditForm({ ...editForm, currentSource: e.target.value })}>
                      {Opt.CURRENT_SOURCE.map((h) => (
                        <option key={h || "e"} value={h}>
                          {h || "—"}
                        </option>
                      ))}
                    </select>
                  </label>
                  {editForm.currentSource !== Opt.NOT_SOURCING_YET && (
                    <label>
                      <span style={{ fontSize: 12, color: "#64748b" }}>Monthly budget</span>
                      <select className="cfl-input" value={editForm.budget} onChange={(e) => setEditForm({ ...editForm, budget: e.target.value })}>
                        {Opt.BUDGET_OPTIONS.map((h) => (
                          <option key={h || "e"} value={h}>
                            {h || "—"}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Challenge</span>
                    <textarea className="cfl-input" rows={3} value={editForm.challenge} onChange={(e) => setEditForm({ ...editForm, challenge: e.target.value })} />
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Reseller program interest</span>
                    <select className="cfl-input" value={editForm.resellerInterest} onChange={(e) => setEditForm({ ...editForm, resellerInterest: e.target.value })}>
                      {Opt.RESELLER_INTEREST.map((h) => (
                        <option key={h || "e"} value={h}>
                          {h || "—"}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    <span style={{ fontSize: 12, color: "#64748b" }}>Admin notes (internal)</span>
                    <textarea className="cfl-input" rows={3} value={editForm.adminNotes} onChange={(e) => setEditForm({ ...editForm, adminNotes: e.target.value })} />
                  </label>
                </div>
              </div>
            </div>
            <div className="cfl-modal-footer">
              <button type="button" className="cfl-muted" onClick={() => { setEditForm(null); setEditId(null); }} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="cfl-primary" onClick={saveEdit} disabled={saving}>
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="cfl-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cfl-delete-title"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div className="cfl-modal cfl-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cfl-modal-header">
              <h3 id="cfl-delete-title" className="cfl-delete-modal-title">
                Delete this lead?
              </h3>
              <button
                type="button"
                className="cfl-modal-close"
                onClick={() => !deleting && setDeleteTarget(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="cfl-modal-body">
              <p className="cfl-delete-warn">
                This removes the submission from the database permanently. This action cannot be undone.
              </p>
              <div className="cfl-delete-summary">
                <div>
                  <span className="cfl-delete-k">Name</span>
                  <span className="cfl-delete-v">{deleteTarget.fullName || "—"}</span>
                </div>
                <div>
                  <span className="cfl-delete-k">Mobile</span>
                  <span className="cfl-delete-v">{deleteTarget.mobile || "—"}</span>
                </div>
                <div>
                  <span className="cfl-delete-k">Email</span>
                  <span className="cfl-delete-v">{deleteTarget.email || "—"}</span>
                </div>
                <div>
                  <span className="cfl-delete-k">Campaign</span>
                  <span className="cfl-delete-v">{deleteTarget.campaignSlug || "—"}</span>
                </div>
                <div>
                  <span className="cfl-delete-k">Submitted</span>
                  <span className="cfl-delete-v">
                    {deleteTarget.createdAt ? moment(deleteTarget.createdAt).format("DD MMM YYYY, HH:mm") : "—"}
                  </span>
                </div>
                <div className="cfl-delete-id-row">
                  <span className="cfl-delete-k">Record ID</span>
                  <code className="cfl-delete-id">{String(deleteTarget._id)}</code>
                </div>
              </div>
            </div>
            <div className="cfl-modal-footer cfl-delete-footer">
              <button type="button" className="cfl-muted" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </button>
              <button type="button" className="cfl-btn-delete-confirm" onClick={confirmDeleteLead} disabled={deleting}>
                {deleting ? "Deleting…" : "Delete permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
